import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, Chip, IconButton, Tooltip, CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SearchBar from '../../components/SearchBar';
import DataTable from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';
import ConfirmDialog from '../../components/ConfirmDialog';
import { getCoupons, deleteCoupon } from '../../api/endpoints/coupons';
import { formatCurrency } from '../../utils/formatters';

const CouponList = () => {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCoupons({ page, limit: rowsPerPage, search: debouncedSearch });
      setCoupons(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await deleteCoupon(deleteId);
      toast.success('Coupon deleted');
      setDeleteId(null);
      fetchCoupons();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to delete coupon');
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    { id: 'code', label: 'Code', sortable: true, render: (row) => <strong>{row.code}</strong> },
    { id: 'discountType', label: 'Discount', render: (row) => (
      row.discountType === 'percentage' ? `${row.discountValue}%` : formatCurrency(row.discountValue)
    )},
    { id: 'minOrderAmount', label: 'Min Order', render: (row) => formatCurrency(row.minOrderAmount) },
    { id: 'usageCount', label: 'Used', render: (row) => `${row.usageCount}${row.maxUses > 0 ? ` / ${row.maxUses}` : ''}` },
    { id: 'validUntil', label: 'Expires', render: (row) => new Date(row.validUntil).toLocaleDateString() },
    { id: 'isActive', label: 'Status', render: (row) => (
      <StatusChip status={row.isActive ? 'active' : 'inactive'} label={row.isActive ? 'Active' : 'Inactive'} />
    )},
    { id: 'actions', label: 'Actions', render: (row) => (
      <Stack direction="row" spacing={0.5}>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => navigate(`/shop/coupons/${row._id}/edit`)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={() => setDeleteId(row._id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    )},
  ];

  return (
    <Box>
      <PageHeader
        title="Coupons"
        subtitle="Manage discount coupons"
        actionLabel="Create Coupon"
        onAction={() => navigate('/shop/coupons/new')}
      />
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search by code..." />
      </Stack>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <DataTable
          columns={columns}
          rows={coupons}
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          onPageChange={setPage}
          onRowsPerPageChange={(v) => { setRowsPerPage(v); setPage(1); }}
        />
      )}
      <ConfirmDialog
        open={!!deleteId}
        title="Delete Coupon"
        content="Are you sure you want to delete this coupon?"
        loading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </Box>
  );
};

export default CouponList;
